import { CalendarDays, Download, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { formatQuarterHours } from '@obraplan/core/domain/calendar/capacity';
import { nationalHolidays } from '@obraplan/core/domain/calendar/holidays';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { useContainer, usePermissionGlobally, useSession } from '@/features/session/use-session';
import { formatBusinessDate } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const WEEKDAY = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'] as const;

/** `"2026-04-21"` → `"terça"`. Data ISO é UTC puro; nenhum fuso entra aqui (RN-120). */
function weekdayOf(date: string): string {
  return WEEKDAY[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? '';
}

/**
 * Calendário de trabalho: os dias que não contam na carga horária.
 *
 * Um feriado aqui é uma exceção com zero hora, e é o que faz a capacidade do mês parar de
 * contar aquele dia — e, com ela, o custo/hora do funcionário (RN-059). Vale para todas as
 * obras: é o calendário do sistema, o fundo da cascata (RN-040).
 */
export function CalendarPage() {
  const container = useContainer();
  const session = useSession();
  const canManage = usePermissionGlobally(PERMISSION.EMPLOYEES_MANAGE);

  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [importOpen, setImportOpen] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [escolhidos, setEscolhidos] = useState<ReadonlySet<string>>(new Set());
  const [novo, setNovo] = useState({ date: '', reason: '', meioPeriodo: false });
  const [recarregar, setRecarregar] = useState(0);

  const view = useLive(
    () => container.getSystemCalendar.execute(session),
    [session.userId, recarregar],
  );

  const dados = view !== undefined && view.ok ? view.value : undefined;

  const doAno = useMemo(
    () =>
      (dados?.exceptions ?? [])
        .filter((excecao) => excecao.date.startsWith(`${String(ano)}-`))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [dados, ano],
  );

  /** Os feriados nacionais saem do domínio, não da rede: são cálculo, não cadastro. */
  const nacionais = useMemo(() => nationalHolidays(ano), [ano]);
  const jaCadastrados = useMemo(
    () => new Set((dados?.exceptions ?? []).map((excecao) => excecao.date)),
    [dados],
  );

  const abrirImportacao = (): void => {
    // Tudo marcado, menos o que já existe: o gesto comum é trazer o ano inteiro.
    setEscolhidos(
      new Set(nacionais.filter((f) => !jaCadastrados.has(f.date)).map((feriado) => feriado.date)),
    );
    setImportOpen(true);
  };

  const concluir = (): void => {
    setImportOpen(false);
    setNovoOpen(false);
    setNovo({ date: '', reason: '', meioPeriodo: false });
    setRecarregar((atual) => atual + 1);
  };

  const [importar, importState] = useCommand(
    () =>
      container.importCalendarExceptions.execute(
        {
          calendarId: dados?.calendar.id,
          exceptions: nacionais
            .filter((feriado) => escolhidos.has(feriado.date))
            .map((feriado) => ({ date: feriado.date, quarterHours: 0, reason: feriado.name })),
        },
        session,
      ),
    {
      successMessage: (resultado) =>
        `${String(resultado.created)} feriados importados${resultado.skipped > 0 ? ` · ${String(resultado.skipped)} já existiam` : ''}.`,
      silent: true,
      onSuccess: concluir,
    },
  );

  const [adicionar, addState] = useCommand(
    () =>
      container.addCalendarException.execute(
        {
          calendarId: dados?.calendar.id,
          date: novo.date,
          quarterHours: novo.meioPeriodo
            ? Math.floor((dados?.calendar.dailyQuarterHours ?? 0) / 2)
            : 0,
          reason: novo.reason,
        },
        session,
      ),
    { successMessage: 'Feriado cadastrado.', silent: true, onSuccess: concluir },
  );

  const [remover] = useCommand(
    (exceptionId: string) => container.removeCalendarException.execute({ exceptionId }, session),
    { successMessage: 'Dia removido do calendário.', onSuccess: concluir },
  );

  if (view === undefined) return <LoadingState label="Carregando o calendário…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;
  if (dados === undefined) return <LoadingState />;

  const anos = [hoje.getUTCFullYear() - 1, hoje.getUTCFullYear(), hoje.getUTCFullYear() + 1];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Calendário</h1>
          <p className="text-(--color-content-muted)">
            Dias que não contam na carga horária, em todas as obras.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={abrirImportacao}>
              <Download aria-hidden="true" />
              Importar feriados de {ano}
            </Button>
            <Button variant="primary" onClick={() => setNovoOpen(true)}>
              <Plus aria-hidden="true" />
              Novo feriado
            </Button>
          </div>
        )}
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-(--color-content-muted)">
              Capacidade deste mês
            </p>
            <p className="tabular text-2xl font-semibold">
              {formatQuarterHours(dados.capacity.monthly)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-(--color-content-muted)">
              Dias úteis
            </p>
            <p className="tabular text-2xl font-semibold">{dados.capacity.workingDays}</p>
          </div>
          <p className="text-sm text-(--color-content-muted)">Já descontando os feriados abaixo.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>
              {doAno.length} {doAno.length === 1 ? 'dia' : 'dias'} em {ano}
            </CardTitle>
            <CardDescription>Zero hora significa que não se trabalha no dia.</CardDescription>
          </div>
          <select
            aria-label="Ano"
            className="h-9 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm"
            value={ano}
            onChange={(evento) => setAno(Number(evento.target.value))}
          >
            {anos.map((opcao) => (
              <option key={opcao} value={opcao}>
                {opcao}
              </option>
            ))}
          </select>
        </CardHeader>
        <CardContent className="p-0">
          {doAno.length === 0 ? (
            <EmptyState
              title={`Nenhum dia cadastrado em ${String(ano)}`}
              description="Importe os feriados nacionais do ano."
              icon={<CalendarDays className="size-8" />}
              {...(canManage
                ? { action: { label: 'Importar feriados', onClick: abrirImportacao } }
                : {})}
            />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Jornada</TableHead>
                    {canManage && (
                      <TableHead className="text-right">
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {doAno.map((excecao) => (
                    <TableRow key={excecao.id}>
                      <TableCell className="tabular whitespace-nowrap">
                        {formatBusinessDate(excecao.date)}
                        <span className="ml-2 text-xs text-(--color-content-muted)">
                          {weekdayOf(excecao.date)}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{excecao.reason}</TableCell>
                      <TableCell>
                        {excecao.quarterHours === 0 ? (
                          <Badge tone="neutral">Não se trabalha</Badge>
                        ) : (
                          <Badge tone="info">{formatQuarterHours(excecao.quarterHours)}</Badge>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remover ${excecao.reason}`}
                            onClick={() => void remover(excecao.id)}
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Feriados nacionais de {ano}</DialogTitle>
            <DialogDescription>
              Carnaval e Corpus Christi são ponto facultativo — desmarque se a obra trabalha.
            </DialogDescription>
          </DialogHeader>

          <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
            {nacionais.map((feriado) => {
              const existe = jaCadastrados.has(feriado.date);
              return (
                <li key={feriado.date}>
                  <label
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                      existe ? 'opacity-60' : 'hover:bg-(--color-surface-muted)'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={existe}
                      checked={escolhidos.has(feriado.date)}
                      onChange={(evento) =>
                        setEscolhidos((atual) => {
                          const proximo = new Set(atual);
                          if (evento.target.checked) proximo.add(feriado.date);
                          else proximo.delete(feriado.date);
                          return proximo;
                        })
                      }
                    />
                    <span className="tabular text-xs text-(--color-content-muted)">
                      {formatBusinessDate(feriado.date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{feriado.name}</span>
                    {!feriado.mandatory && <Badge tone="warn">Facultativo</Badge>}
                    {existe && <Badge tone="neutral">Já cadastrado</Badge>}
                  </label>
                </li>
              );
            })}
          </ul>

          {importState.error !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {importState.error.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setImportOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={escolhidos.size === 0 || importState.isPending}
              onClick={() => void importar()}
            >
              {importState.isPending ? 'Importando…' : `Importar ${String(escolhidos.size)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo feriado</DialogTitle>
            <DialogDescription>Para o feriado municipal ou a parada da empresa.</DialogDescription>
          </DialogHeader>

          <form
            className="flex flex-col gap-4"
            onSubmit={(evento) => {
              evento.preventDefault();
              void adicionar();
            }}
          >
            <Field label="Data" htmlFor="calendario-data" required>
              <Input
                id="calendario-data"
                type="date"
                required
                value={novo.date}
                onChange={(evento) => setNovo((atual) => ({ ...atual, date: evento.target.value }))}
              />
            </Field>
            <Field label="Motivo" htmlFor="calendario-motivo" required>
              <Input
                id="calendario-motivo"
                required
                maxLength={120}
                placeholder="Aniversário da cidade"
                value={novo.reason}
                onChange={(evento) =>
                  setNovo((atual) => ({ ...atual, reason: evento.target.value }))
                }
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={novo.meioPeriodo}
                onChange={(evento) =>
                  setNovo((atual) => ({ ...atual, meioPeriodo: evento.target.checked }))
                }
              />
              Trabalha meio período
            </label>

            {addState.error !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {addState.error.message}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setNovoOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={addState.isPending}>
                {addState.isPending ? 'Salvando…' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
